import type { AuthenticationMethod } from '@adornis/authentication/core/authentication-methods.js';
import { defineAuthenticationMethods } from '@adornis/authentication/core/authentication-methods.js';
import type { InitializedAuthentication } from '@adornis/authentication/core/init-authentication.js';
import { initAuthentication } from '@adornis/authentication/core/init-authentication.js';
import { createMicrosoftAuthenticationMethod } from '@adornis/authentication/methods/microsoft.js';
import { createPasswordAuthenticationMethod } from '@adornis/authentication/methods/password.js';
import {
  type AuthenticationSessionFinalizer,
  createAuthenticationSessionFinalizer,
} from '@adornis/authentication/server/session-finalizer.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import type { MongoDBDatalayer } from '@adornis/mongodb/server/MongoDBDatalayer.js';
import { AmbiguousLoginError, InvalidCredentialsError } from '@adornis/users/db/errors.js';
import { config } from '../config.js';
import { sendAmbiguousAccountsMail } from '../users/server/sendAmbiguousAccountsMail.js';
import { createMicrosoftAccountResolver } from './server/microsoftAccountResolver.js';

/**
 * The built-in username/password method plus the project rule for shared household emails: when
 * the identifier cannot pick one account, the shared inbox gets a mail listing its usernames. That
 * runs server-side where the identifier is known; the browser only ever sees the same wrong-credentials
 * error as for a wrong password, so nobody can probe which addresses are shared.
 */
const createMartinusPasswordMethod = (mongoDBDatalayer: MongoDBDatalayer): AuthenticationMethod => {
  // Person shares the engine's user collection, so the default user class resolves the same documents
  const password = createPasswordAuthenticationMethod({ mongoDBDatalayer, supportedIntents: ['login'] });
  return {
    ...password,
    server: async serverContext => {
      const contribution = await password.server?.(serverContext);
      if (!contribution) throw new Error('The password method did not contribute a provider');
      const provider = contribution.provider;
      return {
        ...contribution,
        provider: {
          ...provider,
          begin: async (providerContext, input) => {
            try {
              return await provider.begin(providerContext, input);
            } catch (error) {
              if (!(error instanceof AmbiguousLoginError)) throw error;
              if (typeof input.username === 'string')
                sendAmbiguousAccountsMail(input.username, 'login').catch((mailError: unknown) =>
                  logger.error({ err: mailError }, 'Mail about ambiguous accounts could not be sent'),
                );
              throw new InvalidCredentialsError();
            }
          },
        },
      };
    },
  };
};

type MartinusAuthentication = InitializedAuthentication<readonly AuthenticationMethod[]>;
let authentication: MartinusAuthentication | undefined;
let sessionFinalizer: AuthenticationSessionFinalizer | undefined;

/**
 * Initializes the one authentication runtime of this app. Runs once per process: the server calls
 * it from `initalizePackages` with its datalayer, the browser from `client/startup.ts` without one.
 * The CLI strips the server branch of `initAuthentication` for the browser, so the datalayer is
 * never read there.
 */
export const initMartinusAuthentication = async (mongoDBDatalayer?: MongoDBDatalayer) => {
  if (authentication) return authentication;
  // SAFETY: only the server branch of initAuthentication reads the datalayer, and the server always passes one.
  const datalayer = mongoDBDatalayer as MongoDBDatalayer;
  const microsoftCredentials = A.isServer
    ? {
        tenantID: config.get('MS_TENANT_ID'),
        clientID: config.get('MS_CLIENT_ID'),
        clientSecret: config.get('MS_CLIENT_SECRET'),
      }
    : { tenantID: '00000000-0000-0000-0000-000000000000', clientID: 'unused', clientSecret: 'unused' };
  authentication = await initAuthentication<readonly AuthenticationMethod[]>({
    mongoDBDatalayer: datalayer,
    methods: defineAuthenticationMethods([
      createMartinusPasswordMethod(datalayer),
      createMicrosoftAuthenticationMethod({
        ...microsoftCredentials,
        // The server-only import is undefined in the client bundle, so construct it only when called.
        accountResolver: request =>
          createMicrosoftAccountResolver({ tenantID: microsoftCredentials.tenantID, mongoDBDatalayer: datalayer })(
            request,
          ),
      }),
    ]),
    authenticationOrigin: new URL(baseConfig.get('ROOT_URL')).origin,
    authenticationPagePath: '/',
  });
  // server-only import, undefined in the client bundle
  if (A.isServer) sessionFinalizer = createAuthenticationSessionFinalizer(datalayer);
  return authentication;
};

/**
 * Logs the current session in as the given account, the same last step a password login ends with. For flows
 * that already proved the account by other means, such as the signed link of the SuS update mail.
 */
export const getMartinusSessionFinalizer = (): AuthenticationSessionFinalizer => {
  if (!sessionFinalizer) throw new Error('initMartinusAuthentication() has not run on the server yet');
  return sessionFinalizer;
};

export const getMartinusAuthentication = (): MartinusAuthentication => {
  if (!authentication) throw new Error('initMartinusAuthentication() has not run yet');
  return authentication;
};
